import React, { useState, useCallback } from 'react';
import { Upload, X, Image, Loader2, AlertCircle, Check, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ImageUploader - Drag and drop image upload component
 * 
 * Props:
 * - category: 'posters' | 'logos' | 'partners'
 * - onUpload: (result) => void - Called when upload succeeds
 * - currentUrl: string - Current image URL (for preview)
 * - label: string - Label for the upload area
 * - multiple: boolean - Allow multiple file selection
 * - className: string - Additional CSS classes
 */
const ImageUploader = ({
  category = 'posters',
  onUpload,
  currentUrl,
  label = 'Upload Image',
  multiple = false,
  className = '',
  maxFiles = 5
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  }, [category, multiple, maxFiles]);

  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  }, [category, multiple, maxFiles]);

  const handleFiles = async (files) => {
    setError(null);
    
    // Filter for valid image types
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    const validFiles = files.filter(f => validTypes.includes(f.type));
    
    if (validFiles.length === 0) {
      setError('Please select valid image files (JPG, PNG, GIF, WebP, SVG)');
      return;
    }
    
    if (!multiple && validFiles.length > 1) {
      validFiles.length = 1; // Only use first file
    }
    
    if (multiple && validFiles.length > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed`);
      validFiles.length = maxFiles;
    }
    
    // Check file sizes (max 10MB each)
    const oversized = validFiles.filter(f => f.size > 10 * 1024 * 1024);
    if (oversized.length > 0) {
      setError('Some files exceed the 10MB limit');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      if (multiple) {
        // Upload multiple files
        const formData = new FormData();
        validFiles.forEach(file => formData.append('files', file));
        
        const response = await fetch(`${API}/api/uploads/multiple-images?category=${category}`, {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Upload failed');
        }
        
        const data = await response.json();
        
        if (data.successful > 0) {
          const successfulUploads = data.results.filter(r => r.success);
          onUpload?.(successfulUploads);
          toast.success(`${data.successful} image(s) uploaded successfully`);
        }
        
        if (data.failed > 0) {
          toast.error(`${data.failed} upload(s) failed`);
        }
      } else {
        // Upload single file
        const formData = new FormData();
        formData.append('file', validFiles[0]);
        
        const response = await fetch(`${API}/api/uploads/event-image?category=${category}`, {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Upload failed');
        }
        
        const data = await response.json();
        
        if (data.success) {
          onUpload?.(data);
          toast.success('Image uploaded successfully');
        } else {
          throw new Error(data.error || 'Upload failed');
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload image');
      toast.error(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className={`${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      
      {/* Current Image Preview */}
      {currentUrl && !uploading && (
        <div className="mb-3 relative group">
          <img
            src={currentUrl.startsWith('http') ? currentUrl : `${API}${currentUrl}`}
            alt="Current"
            className="w-full h-40 object-cover rounded-lg border border-gray-200"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDE2IDEwIDUgMjEiPjwvcG9seWxpbmU+PC9zdmc+';
            }}
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
            <span className="text-white text-sm">Drop new image to replace</span>
          </div>
        </div>
      )}
      
      {/* Drag & Drop Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
          transition-all duration-200
          ${isDragging 
            ? 'border-green-500 bg-green-50' 
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }
          ${uploading ? 'pointer-events-none opacity-75' : ''}
        `}
        data-testid={`image-uploader-${category}`}
      >
        <input
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
          multiple={multiple}
          onChange={handleFileSelect}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={uploading}
          data-testid={`file-input-${category}`}
        />
        
        {uploading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="w-10 h-10 text-green-500 animate-spin mb-2" />
            <p className="text-sm text-gray-600">Uploading...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <Upload className="w-10 h-10 text-gray-400 mb-2" />
            <p className="text-sm text-gray-600 mb-1">
              {isDragging ? 'Drop image here' : 'Drag & drop or click to upload'}
            </p>
            <p className="text-xs text-gray-400">
              JPG, PNG, GIF, WebP, SVG up to 10MB
              {multiple && ` (max ${maxFiles} files)`}
            </p>
          </div>
        )}
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="mt-2 flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};


/**
 * PartnerLogosUploader - Upload and manage multiple partner logos
 */
const PartnerLogosUploader = ({
  logos = [],
  onUpdate,
  maxLogos = 10
}) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = (results) => {
    if (Array.isArray(results)) {
      const newLogos = results.map(r => r.url || r.path);
      onUpdate?.([...logos, ...newLogos].slice(0, maxLogos));
    } else if (results?.url || results?.path) {
      const newUrl = results.url || results.path;
      onUpdate?.([...logos, newUrl].slice(0, maxLogos));
    }
  };

  const removeLogo = (index) => {
    const updated = logos.filter((_, i) => i !== index);
    onUpdate?.(updated);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Partner/Sponsor Logos
      </label>
      
      {/* Current Logos Grid */}
      {logos.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-3">
          {logos.map((logo, index) => (
            <div key={index} className="relative group">
              <img
                src={logo.startsWith('http') ? logo : `${API}${logo}`}
                alt={`Partner ${index + 1}`}
                className="w-full h-20 object-contain rounded-lg border border-gray-200 bg-white p-2"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                }}
              />
              <button
                type="button"
                onClick={() => removeLogo(index)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`remove-logo-${index}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Add More */}
      {logos.length < maxLogos && (
        <ImageUploader
          category="partners"
          onUpload={handleUpload}
          multiple={true}
          maxFiles={maxLogos - logos.length}
          label=""
          className="mt-2"
        />
      )}
      
      <p className="text-xs text-gray-400 mt-2">
        {logos.length}/{maxLogos} logos added
      </p>
    </div>
  );
};


/**
 * EventMediaUploader - Combined component for all event media
 */
const EventMediaUploader = ({
  heroImageUrl,
  logoUrl,
  partnerLogos = [],
  onHeroImageChange,
  onLogoChange,
  onPartnerLogosChange
}) => {
  return (
    <div className="space-y-6">
      {/* Event Poster/Hero Image */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <Image className="w-5 h-5" />
          Event Poster / Hero Image
        </h4>
        <ImageUploader
          category="posters"
          currentUrl={heroImageUrl}
          onUpload={(result) => {
            const url = result.url || result.path;
            onHeroImageChange?.(url);
          }}
          label=""
        />
        {heroImageUrl && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onHeroImageChange?.(null)}
            className="mt-2 text-red-600 hover:text-red-700"
          >
            <X className="w-4 h-4 mr-1" />
            Remove Image
          </Button>
        )}
      </div>
      
      {/* Event Logo */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <Image className="w-5 h-5" />
          Event Logo
        </h4>
        <ImageUploader
          category="logos"
          currentUrl={logoUrl}
          onUpload={(result) => {
            const url = result.url || result.path;
            onLogoChange?.(url);
          }}
          label=""
        />
        {logoUrl && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onLogoChange?.(null)}
            className="mt-2 text-red-600 hover:text-red-700"
          >
            <X className="w-4 h-4 mr-1" />
            Remove Logo
          </Button>
        )}
      </div>
      
      {/* Partner Logos */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <Image className="w-5 h-5" />
          Partner / Sponsor Logos
        </h4>
        <PartnerLogosUploader
          logos={partnerLogos}
          onUpdate={onPartnerLogosChange}
          maxLogos={10}
        />
      </div>
    </div>
  );
};

export { ImageUploader, PartnerLogosUploader, EventMediaUploader };
export default ImageUploader;
